/**
 * 3D Room Visualizer — Seams
 *
 * Where two Wall&CeilingBoard boards meet there is a narrow groove. It is real
 * geometry on the walls, but under a tenth of an inch wide: far too fine for the
 * lighting to pick out, so on a brightly lit wall the joints vanish and the wall
 * reads as one sheet. A seam is drawn over each joint as a thin dark line of the
 * groove's true width — the shadow the groove would hold — so it thins out with
 * distance exactly as a real seam does, instead of staying a hairline.
 *
 * This file only works out WHERE the seams go, from the same board rectangles
 * the meshes are built from; the mesh builders turn them into geometry, and the
 * render settings decide how dark and how wide they are (hidden unless the
 * designer's render settings switch them on — the wizard's viewers never show
 * them).
 */

import { BoardRect } from './types';

/** One seam: a straight line on the surface (feet, surface coordinates). */
export interface SeamLine {
  /** 'x' = the seam runs along X (between courses of horizontal boards); 'y' = it runs up Y. */
  along: 'x' | 'y';
  /** Where the line starts and ends, along its own direction. */
  from: number;
  to: number;
  /** Where the line sits on the other axis. */
  at: number;
}

const TOUCH = 1e-4;

export interface SeamOptions {
  /** Boards run up the wall (courses step across X) rather than along it (courses step up Y). */
  vertical: boolean;
  /**
   * How far past the nominal course line the groove between two courses sits
   * (feet), measured into the further board. The extruded profile puts it about
   * an inch in; flat boards (the ceiling) have it on the line (0, the default).
   */
  grooveOffset?: number;
}

/**
 * Seams for every joint among `boards` (one section, one direction): the long
 * joints between courses, and the butt joints where two boards meet end to end
 * in the same course. A joint is only as long as the two boards actually touch —
 * a board over a window gets no seam along the window.
 *
 * A board notched around a window or door is drawn as several rectangles, but
 * it is still one board, cut to shape: there is no joint between its own pieces
 * (they carry the same `boardId`), so no seam is drawn there.
 */
export function seamLines(boards: BoardRect[], options: SeamOptions): SeamLine[] {
  const { vertical } = options;
  const grooveOffset = options.grooveOffset ?? 0;
  // "across" is the way courses stack; "along" is the way a board runs.
  const across0 = (b: BoardRect) => (vertical ? b.x : b.y);
  const acrossSize = (b: BoardRect) => (vertical ? b.width : b.height);
  const along0 = (b: BoardRect) => (vertical ? b.y : b.x);
  const alongSize = (b: BoardRect) => (vertical ? b.height : b.width);
  const boardAxis: 'x' | 'y' = vertical ? 'y' : 'x';
  const courseAxis: 'x' | 'y' = vertical ? 'x' : 'y';

  const seams: SeamLine[] = [];
  for (const a of boards) {
    if (alongSize(a) <= TOUCH || acrossSize(a) <= TOUCH) continue;
    for (const b of boards) {
      if (a === b || alongSize(b) <= TOUCH || acrossSize(b) <= TOUCH) continue;
      if (a.boardId !== undefined && a.boardId === b.boardId) continue; // two pieces of one board

      // b is the next course beyond a: a seam along the boards.
      if (Math.abs(across0(b) - (across0(a) + acrossSize(a))) < TOUCH) {
        const from = Math.max(along0(a), along0(b));
        const to = Math.min(along0(a) + alongSize(a), along0(b) + alongSize(b));
        if (to - from > TOUCH) seams.push({ along: boardAxis, from, to, at: across0(b) + grooveOffset });
      }

      // b carries on from the end of a, in the same course: a seam across the boards.
      if (Math.abs(along0(b) - (along0(a) + alongSize(a))) < TOUCH) {
        const from = Math.max(across0(a), across0(b));
        const to = Math.min(across0(a) + acrossSize(a), across0(b) + acrossSize(b));
        if (to - from > TOUCH) seams.push({ along: courseAxis, from, to, at: along0(b) });
      }
    }
  }
  return seams;
}
